import { forwardRef, type ElementType, type HTMLAttributes } from 'react';

import { cn } from '@ui/lib/utils';

export enum TypographyVariant {
  DEFAULT = 'default',
  STRONG = 'strong',
  SECONDARY = 'secondary',
  MUTED = 'muted',
  FAINT = 'faint',
  PRIMARY = 'primary',
  DESTRUCTIVE = 'destructive',
  SUCCESS = 'success',
  WARNING = 'warning',
  OVERLINE = 'overline',
}

export type TypographySize =
  | '3xs'
  | '2xs'
  | 'xs-plus'
  | 'xs'
  | 'sm-tight'
  | 'sm'
  | 'md'
  | 'base'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl';

export type TypographyTag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';

interface TypographyStyle {
  variant?: TypographyVariant;
  size?: TypographySize;
  as?: TypographyTag;
  bold?: boolean;
}

interface TypographyProps extends HTMLAttributes<HTMLElement>, TypographyStyle {
  italic?: boolean;
}

const VARIANT_CLASSNAMES: Record<TypographyVariant, string> = {
  [TypographyVariant.DEFAULT]: '',
  [TypographyVariant.STRONG]: 'text-fg-strong',
  [TypographyVariant.SECONDARY]: 'text-fg',
  [TypographyVariant.MUTED]: 'text-fg-muted',
  [TypographyVariant.FAINT]: 'text-fg-faint',
  [TypographyVariant.PRIMARY]: 'text-primary',
  [TypographyVariant.DESTRUCTIVE]: 'text-destructive',
  [TypographyVariant.SUCCESS]: 'text-gain',
  [TypographyVariant.WARNING]: 'text-warn-deep',
  [TypographyVariant.OVERLINE]: 'tracking-wider text-fg-muted uppercase',
};

const SIZE_CLASSNAMES: Record<TypographySize, string> = {
  '3xs': 'text-3xs',
  '2xs': 'text-2xs',
  'xs-plus': 'text-xs-plus',
  xs: 'text-xs',
  'sm-tight': 'text-sm-tight',
  sm: 'text-sm',
  md: 'text-md',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
};

const TAG_CLASSNAMES: Record<TypographyTag, string> = {
  h1: 'tracking-tight',
  h2: 'tracking-tight',
  h3: '',
  h4: '',
  p: '',
  span: '',
};

const TAG_SIZES: Record<TypographyTag, TypographySize> = {
  h1: '4xl',
  h2: '3xl',
  h3: '2xl',
  h4: 'xl',
  p: 'sm',
  span: 'sm',
};

const HEADING_TAGS: TypographyTag[] = ['h1', 'h2', 'h3', 'h4'];
const OVERLINE_SIZE: TypographySize = '2xs';

const getFontWeightClass = (variant: TypographyVariant, tag: TypographyTag, bold?: boolean): string => {
  if (variant === TypographyVariant.OVERLINE) {
    return 'font-extrabold';
  }
  return (bold ?? HEADING_TAGS.includes(tag)) ? 'font-bold' : 'font-normal';
};

export const getTypographyClassName = ({
  variant = TypographyVariant.DEFAULT,
  size,
  as: tag = 'p',
  bold,
}: TypographyStyle = {}): string =>
  cn(
    SIZE_CLASSNAMES[size ?? (variant === TypographyVariant.OVERLINE ? OVERLINE_SIZE : TAG_SIZES[tag])],
    TAG_CLASSNAMES[tag],
    VARIANT_CLASSNAMES[variant],
    getFontWeightClass(variant, tag, bold)
  );

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, size, as: tag = 'p', bold, italic, ...props }, ref) => {
    const Tag = tag as ElementType;
    return (
      <Tag
        ref={ref}
        data-slot="typography"
        className={cn(
          'my-0 text-left',
          getTypographyClassName({ variant, size, as: tag, bold }),
          italic && 'italic',
          className
        )}
        {...props}
      />
    );
  }
);

Typography.displayName = 'Typography';
